import React from 'react';
import VerticalTab from '../ui/VerticalTab';
import useDocumentManager from '../../hooks/useDocumentManager';

/**
 * Document tabs navigation component
 * Displays vertical tabs for all open documents
 * 
 * @returns {JSX.Element} - Rendered component
 */
function DocumentTabs() {
  const {
    documents,
    activeDocumentId,
    setActiveDocument,
    closeDocument,
    createDocument
  } = useDocumentManager();
  
  const handleTabClick = (documentId) => {
    setActiveDocument(documentId);
  };
  
  const handleCloseTab = (e, documentId) => {
    e.stopPropagation(); // Prevent tab selection when closing
    closeDocument(documentId);
  };
  
  const handleCreateDocument = () => {
    createDocument({ 
      title: `New Document ${documents.length + 1}` 
    });
  };
  
  return (
    <div className="document-tabs-container" style={{
      display: 'flex',
      flexDirection: 'column',
      width: '180px',
      backgroundColor: '#fafafa',
      borderRight: '1px solid #e0e0e0',
      padding: '12px 0',
      height: '100%',
      boxShadow: '0 0 8px rgba(0,0,0,0.05)'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '0 12px 12px 12px',
        borderBottom: '1px solid #eee'
      }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '14px',
          fontWeight: 'normal',
          color: '#555'
        }}>Documents</h2>
        
        <button 
          onClick={handleCreateDocument}
          style={{
            backgroundColor: '#0078d4',
            color: 'white',
            border: 'none',
            borderRadius: '3px',
            padding: '4px 10px',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          + New
        </button>
      </div>
      
      <div style={{ 
        flex: 1, 
        overflowY: 'auto',
        padding: '12px' 
      }}>
        {documents.length === 0 ? (
          <div style={{ 
            padding: '12px', 
            color: '#888',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            No documents open.<br />
            Click "New" to create one.
          </div>
        ) : (
          documents.map(doc => (
            <VerticalTab
              key={doc.id}
              title={doc.title}
              isActive={doc.id === activeDocumentId}
              isDirty={doc.isDirty}
              colorCode={doc.colorCode}
              onClick={() => handleTabClick(doc.id)}
              onClose={(e) => handleCloseTab(e, doc.id)}
            />
          ))
        )}
      </div>
      
      <div style={{ 
        padding: '12px', 
        borderTop: '1px solid #eee',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <button 
          onClick={() => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.txt';
            fileInput.onchange = (e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                  const content = event.target.result;
                  // Determine if it's a survey file based on content
                  const isSurvey = content.includes('--------------------') && 
                                  content.includes('.qt') && 
                                  content.includes('*)');
                  
                  // Create a new document with the file content
                  createDocument({
                    title: file.name.replace(/\.\w+$/, ''),
                    content: content,
                    type: isSurvey ? 'survey' : 'document' 
                  });
                };
                reader.readAsText(file);
              }
            };
            fileInput.click();
          }}
          style={{
            backgroundColor: 'transparent',
            border: '1px solid #0078d4',
            color: '#0078d4',
            borderRadius: '3px',
            padding: '6px 12px',
            fontSize: '12px',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Open File...
        </button>
      </div>
    </div>
  );
}

export default DocumentTabs;